import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { ACCENT, api, errorMessage } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioShell from "./StudioShell";

// Artist-side controls for the public artist page: make the page public or
// private, and choose which published tracks are public. Licensing is managed in Monetization.

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${checked ? "" : "bg-gray-300"}`}
      style={checked ? { backgroundColor: ACCENT } : undefined}
    >
      <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

function TrackCard({ track, onChange }) {
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const setPublic = (value) =>
    run(async () => {
      await api.post("tracks/visibility", { track_id: track.id, is_public: value });
      onChange({ ...track, is_public: value });
    });

  const published = track.status === "published";
  return (
    <li className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 truncate">{track.title}</p>
          <p className="text-xs text-gray-500 capitalize">
            {track.status.replace("_", " ")}
            {!published && " · only published tracks can be public"}
          </p>
        </div>
        <span className="text-sm text-gray-600">{track.is_public ? "Public" : "Private"}</span>
        <Toggle checked={track.is_public} disabled={busy || (!published && !track.is_public)} onChange={setPublic} label={`Show ${track.title} on public page`} />
      </div>

      <Link to={`/artist-studio/monetization?track=${track.id}`} className="mt-2 inline-block text-sm font-semibold underline" style={{ color: ACCENT }}>
        Manage licensing
      </Link>
    </li>
  );
}

function Manager() {
  const [state, setState] = useState({ loading: true, failed: false, data: null });
  const [savingProfile, setSavingProfile] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, failed: false, data: null });
    try {
      const response = await api.get("tracks");
      setState({ loading: false, failed: false, data: response.data?.result });
    } catch (error) {
      setState({ loading: false, failed: true, data: null });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading) {
    return <div className="flex items-center justify-center py-16"><Loading /></div>;
  }
  if (state.failed || !state.data) {
    return (
      <div>
        <p className="text-gray-600 mb-3">We couldn't load your public page settings.</p>
        <button type="button" onClick={load} className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Try again</button>
      </div>
    );
  }

  const { data } = state;
  const setProfilePublic = async (value) => {
    if (savingProfile) return;
    setSavingProfile(true);
    try {
      await api.post("visibility", { is_public: value });
      setState((s) => ({ ...s, data: { ...s.data, is_public: value } }));
      toast.success(value ? "Your artist page is now public." : "Your artist page is now private.");
    } catch (error) {
      toast.error(errorMessage(error, "Could not change visibility."));
    } finally {
      setSavingProfile(false);
    }
  };
  const updateTrack = (updated) =>
    setState((s) => ({ ...s, data: { ...s.data, tracks: s.data.tracks.map((t) => (t.id === updated.id ? updated : t)) } }));

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 flex items-center gap-4">
        <div className="flex-1">
          <p className="font-bold text-gray-900">Public artist page</p>
          <p className="text-sm text-gray-500">
            {data.is_public ? "Anyone with the link can see your profile and public tracks." : "Your page is hidden. Visitors will see “Profile not found”."}
          </p>
          {data.is_public && (
            <Link to={`/artists/${data.slug}`} className="text-sm font-semibold underline" style={{ color: ACCENT }}>
              View public page
            </Link>
          )}
        </div>
        <Toggle checked={data.is_public} disabled={savingProfile} onChange={setProfilePublic} label="Make artist page public" />
      </div>

      <section aria-label="Your tracks">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Tracks on your public page</h2>
        {data.tracks.length === 0 ? (
          <p className="text-gray-500 rounded-2xl border border-dashed border-gray-200 px-4 py-8 text-center">You don't have any tracks yet.</p>
        ) : (
          <ul className="space-y-3">
            {data.tracks.map((t) => (
              <TrackCard key={t.id} track={t} onChange={updateTrack} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default function PublicPageSettings() {
  const account = useStudioAccount();
  const { loading, failed, isApproved, isDeactivated } = account;
  let body;
  if (loading) body = <div className="flex items-center justify-center py-24"><Loading /></div>;
  else if (failed) body = <p className="text-gray-600">We couldn't load your artist account. Please refresh and try again.</p>;
  else if (!isApproved) {
    body = (
      <p className="text-gray-600">
        This page is available once your artist account is approved.{" "}
        <Link to="/artist-studio" className="underline font-semibold">Go to Artist Studio</Link>
      </p>
    );
  } else if (isDeactivated) {
    body = <p className="text-gray-600">Your artist account is deactivated, so your public page is not available.</p>;
  } else body = <Manager />;

  return (
    <StudioShell active="public">
      <div className="w-full max-w-2xl">
        <h1 className="text-[28px] leading-tight font-bold text-gray-900 mb-6">Public Page</h1>
        {body}
      </div>
    </StudioShell>
  );
}
